/**

 * Shared agent status tile — same style as Status page. Used by Status and WidgetAgentPresence.

 */

import { Tooltip } from './Tooltip';

import { formatIpscapeStatusLabel, formatStatusDuration } from '../lib/formatAgentStatus';



export interface AgentStatusRow {

  id: string;

  name: string;

  email?: string;

  team: string;

  campaign: string;

  /** Codes from UC agent-state; used when catalog resolves names / filters. */

  campaign_codes?: string[];

  pause_reason?: string | null;

  ipscapeStatus: string;

  wildixStatus: string;

  statusDuration?: number;

  avatar_url?: string | null;

  /** Platform GET /v3.0/users isLoggedIn (synced periodically). */

  isLoggedIn?: boolean;

}



export function ragColor(status: string): string {

  const s = status.toLowerCase();

  if (['available', 'online', 'registered'].includes(s)) return '#22c55e';

  if (['away', 'dnd', 'unavailable'].includes(s)) return '#eab308';

  if (['busy', 'talking'].includes(s)) return '#ef4444';

  return 'var(--text-muted)';

}



interface AgentTileProps {

  agent: AgentStatusRow;

  avatarUrl?: string;

  /** Compact mode for dashboard widget (smaller padding/font) */

  compact?: boolean;

  /** If true, tile does not stretch to fill grid cell (fixed content height) */

  noStretch?: boolean;

  /** Fill widget content area (individual-agent dashboard tile) */

  fill?: boolean;

  /** Hide name line when the widget chrome already shows the agent name */

  hideName?: boolean;

  /** Hide team subtitle */

  hideTeam?: boolean;

  /** Resolved campaign names line (catalog titles); omit to hide campaigns row */

  campaignDisplay?: string;

  /** Wallboard not live (closed hours / polling off) — muted snapshot styling */

  snapshotMode?: boolean;

}



export default function AgentTile({

  agent,

  avatarUrl,

  compact,

  noStretch,

  fill,

  hideName,

  hideTeam,

  campaignDisplay,

  snapshotMode,

}: AgentTileProps) {

  const imgSrc = avatarUrl || agent.avatar_url || undefined;

  const initials = agent.name.split(/\s+/).map((n) => n[0]).slice(0, 2).join('').toUpperCase();

  const ipscapeColor = snapshotMode ? '#64748b' : ragColor(agent.ipscapeStatus);

  const wildixColor = snapshotMode ? '#64748b' : ragColor(agent.wildixStatus);

  const statusLabel = formatIpscapeStatusLabel(agent.ipscapeStatus, agent.pause_reason);

  const durationLabel = snapshotMode
    ? 'snapshot'
    : formatStatusDuration(agent.statusDuration ?? 0);

  const dashboardSingle = Boolean(fill && hideName);



  const avatarSize = dashboardSingle ? 'clamp(40px, 22cqi, 72px)' : compact ? 32 : 40;

  const padding = dashboardSingle ? 'clamp(0.5rem, 4cqi, 1rem)' : compact ? '0.5rem' : '1rem';

  const fontSize = compact ? '0.75rem' : '0.875rem';

  const statusFontSize = dashboardSingle ? 'clamp(0.9rem, 6cqi, 1.2rem)' : compact ? '0.7rem' : '0.8125rem';

  const metaFontSize = dashboardSingle ? 'clamp(0.78rem, 4.5cqi, 1rem)' : compact ? '0.65rem' : '0.75rem';

  const gap = dashboardSingle ? 'clamp(0.5rem, 3cqi, 0.85rem)' : compact ? '0.5rem' : '0.75rem';



  const tooltipParts = [

    `ipSCAPE: ${agent.ipscapeStatus}`,

    `Wildix: ${agent.wildixStatus}`,

  ];

  if (agent.pause_reason) tooltipParts.push(`Pause: ${agent.pause_reason}`);

  if (campaignDisplay) tooltipParts.push(`Campaigns: ${campaignDisplay}`);

  else if (agent.campaign_codes?.length) tooltipParts.push(`Campaigns: ${agent.campaign_codes.join(', ')}`);



  const wildixLabel = formatIpscapeStatusLabel(agent.wildixStatus);



  const avatarEl = (

    <div

      style={{

        width: avatarSize,

        height: avatarSize,

        borderRadius: '50%',

        overflow: 'hidden',

        background: 'var(--bg)',

        flexShrink: 0,

      }}

    >

      {imgSrc ? (

        <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

      ) : (

        <div

          style={{

            width: '100%',

            height: '100%',

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'center',

            fontSize: dashboardSingle ? 'clamp(0.75rem, 4cqi, 1rem)' : compact ? '0.7rem' : '0.875rem',

            color: 'var(--text-muted)',

            fontWeight: 600,

          }}

        >

          {initials}

        </div>

      )}

    </div>

  );



  const statusLine = (

    <div style={{ fontSize: statusFontSize, color: 'var(--text-muted)', lineHeight: 1.35 }}>

      <span style={{ color: ipscapeColor, fontWeight: 600 }}>
        {snapshotMode ? 'Snapshot · ' : ''}
        {statusLabel}
      </span>

      <span style={{ fontWeight: 500 }}> · {durationLabel}</span>

    </div>

  );



  const metaLine = (

    <div

      style={{

        fontSize: metaFontSize,

        color: 'var(--text-muted)',

        lineHeight: 1.4,

        overflow: 'hidden',

        textOverflow: 'ellipsis',

        whiteSpace: dashboardSingle ? 'normal' : 'nowrap',

        display: dashboardSingle ? '-webkit-box' : undefined,

        WebkitLineClamp: dashboardSingle ? 2 : undefined,

        WebkitBoxOrient: dashboardSingle ? 'vertical' : undefined,

      }}

    >

      <span>

        Wildix: <span style={{ color: wildixColor, fontWeight: 500 }}>{wildixLabel}</span>

      </span>

      {campaignDisplay ? (

        <span>

          <br />

          {campaignDisplay}

        </span>

      ) : null}

    </div>

  );



  const tileBody = dashboardSingle ? (

    <div

      style={{

        display: 'flex',

        alignItems: 'center',

        gap,

        minWidth: 0,

        flex: 1,

        width: '100%',

      }}

    >

      {avatarEl}

      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 'clamp(0.2rem, 2cqi, 0.45rem)' }}>

        {statusLine}

        {metaLine}

      </div>

    </div>

  ) : (

    <>

      <div

        style={{

          display: 'flex',

          alignItems: 'center',

          gap,

          marginBottom: compact ? '0.25rem' : '0.5rem',

        }}

      >

        {avatarEl}

        <div style={{ minWidth: 0, flex: 1 }}>

          {!hideName ? (

            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize }}>

              {agent.name}

            </div>

          ) : null}

          {!hideTeam && agent.team ? (

            <div style={{ fontSize: compact ? '0.7rem' : '0.8125rem', color: 'var(--text-muted)' }}>{agent.team}</div>

          ) : null}

        </div>

      </div>

      {statusLine}

      {campaignDisplay || fill ? metaLine : null}

    </>

  );



  return (

    <Tooltip content={tooltipParts.join(' · ')} placement="top" fillContainer>

      <div

        style={{

          background: 'var(--widget-bg)',

          borderRadius: '8px',

          padding,

          opacity: snapshotMode ? 0.62 : 1,

          filter: snapshotMode ? 'saturate(0.35)' : undefined,

          borderLeft: `4px solid ${ipscapeColor}`,

          borderRight: fill ? undefined : `4px solid ${wildixColor}`,

          minHeight: noStretch && !fill ? undefined : '100%',

          height: noStretch && !fill ? 'fit-content' : '100%',

          width: fill ? '100%' : undefined,

          boxSizing: 'border-box',

          display: fill ? 'flex' : undefined,

          flexDirection: fill ? 'column' : undefined,

          justifyContent: fill ? 'center' : undefined,

          alignItems: fill ? 'stretch' : undefined,

          overflow: 'hidden',

          containerType: fill ? 'size' : undefined,

          containerName: fill ? 'agent-tile' : undefined,

        }}

      >

        {tileBody}

      </div>

    </Tooltip>

  );

}


